import { FilterDropdown, SearchDropdown } from "admin/ui/base";
import React, { useCallback, useEffect, useState } from "react";
import { AppButton } from "site/ui";
import styled from "styled-components";
import { CourseCard, CourseCardProps } from "./CourseCard";
import { ICourse, ICourseStat, IPaginateRequest } from "utils/types";
import { courseStatusOptions, courseTypeOptions, ICourseStatusOptionID, ICourseTypeMenuOptionID, lastDateModifiedOptions, searchOptions } from "./data";
import { Dropdown } from "antd";


const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 20px 40px;
  gap: 24px;
  font-family: "Manrope", sans-serif;

  @media (max-width: 768px) {
    padding: 15px;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`;

const Title = styled.h1`
  font-family: Manrope-Medium;
  font-size: 24px;
  line-height: 32px;
  color: #333;
  margin: 0;
`;

const StatsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 16px;
  width: 100%;
  font-family: "Manrope", sans-serif;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const StatCard = styled.div`
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0px 2px 8px rgba(0, 0, 0, 0.05);
`;

const StatLabel = styled.p`
  font-family: "Manrope", sans-serif;
  font-size: 14px;
  line-height: 20px;
  color: #666;
  margin: 0 0 8px 0;
`;

const StatValue = styled.p`
  font-family: Manrope-Medium;
  font-size: 28px;
  line-height: 36px;
  color: #333;
  margin: 0;
`;

const FiltersContainer = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    gap: 10px;
  }
`;

const SelectContainer = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    gap: 10px;
  }
`;

const SearchContainer = styled.div`
  width: 600px;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const EmptyStateContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 0;
  text-align: center;
`;

const EmptyStateIcon = styled.div`
  width: 48px;
  height: 48px;
  background-color: #eef2ff;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 24px;
  color: #5575f7;
`;

const EmptyStateText = styled.p`
  font-size: 14px;
  color: #666;
  max-width: 300px;
  margin: 8px 0 24px 0;
`;

const CoursesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 24px;
  width: 100%;
`;


export interface CoursesProps {
  onGetCourses: (data: { course?: Partial<ICourse>; search?: string; pagination: IPaginateRequest}) => Promise<ICourse[]>;
  onGetCoursesStat: () => Promise<ICourseStat|undefined>;
  onMoveToCreateCourse: () => void;
  onMoveToCreateInterview: () => void;
  courseCardProps: Omit<CourseCardProps, "course">
}

export interface IFilter {
  courseType: ICourseTypeMenuOptionID;
  courseStatus: ICourseStatusOptionID;
}
const statObject:{id: keyof ICourseStat; label: string}[] = [
  {id: "total", label: "Total courses"},
  {id: "premium", label: "Premium courses"},
  {id: "free", label: "Free courses"},
  {id: "active", label: "Active courses"},
  {id: "totalQuizzes", label: "Total quizzes"},
]
export const Courses: React.FC<CoursesProps> = ({
  onGetCourses, onGetCoursesStat, onMoveToCreateCourse,
  courseCardProps, onMoveToCreateInterview,
}) => {

  const [loading, setLoading] = useState(false)
  const [search, setSearch] = useState("")
  const [courses, setCourses] = useState<ICourse[]>([])
  const [stat, setStat] = useState<ICourseStat>()
  const [pagination, setPagination] = useState<IPaginateRequest>({
    limit: 100, skip: 0
  })
  const [filter, setFilter] = useState<IFilter>({
    courseStatus: "all", courseType: "all"
  })

  const getCourses = useCallback(() => {
    setLoading(true)
    const course:Partial<ICourse> = {}
    if(filter.courseStatus !== "all"){
      course.courseStatus = filter.courseStatus
    }
    if(filter.courseType !== "all"){
      course.courseType = filter.courseType;
    }
    onGetCourses({search, course, pagination}).then(setCourses)
    .finally(() => setLoading(false))
  }, [JSON.stringify(pagination), search, JSON.stringify(filter)])
  useEffect(() => {
    getCourses();
  }, [JSON.stringify(pagination), search, JSON.stringify(filter)])

  useEffect(() => {
    onGetCoursesStat().then(setStat)
  }, [])
  console.log(courses)
  return (
    <Container>
      <Header>
        <Title>Course management</Title>
        {!loading && courses.length > 0 && (
          <Dropdown {...{
            menu: {
              style: {
                padding: 20,
                borderRadius: 24,
              },
              items: [
                {
                  key: "course",
                  label: <div style={{fontSize: 16}}>New course</div>,
                  onClick: onMoveToCreateCourse,
                },
                {
                  key: "interview",
                  label: <div style={{fontSize: 16}}>New interview</div>,
                  onClick: onMoveToCreateInterview,
                }
              ],
            },
            trigger: ["click"],
          }}>
            <AppButton variant="primary" >
              Create new course / interview
            </AppButton>
          </Dropdown>
        )}
      </Header>

      <StatsContainer>
        {statObject.map((item, index) => {
          return (
            <StatCard key={`${item.label}---${index}`}>
              <StatLabel>{item.label}</StatLabel>
              <StatValue>{stat?.[item.id] || 0}</StatValue>
            </StatCard>
          )
        })}
      </StatsContainer>

      <FiltersContainer>
        <SelectContainer>
          <FilterDropdown<ICourseTypeMenuOptionID>
            options={courseTypeOptions}
            placeholder="Course type"
            defaultValue="Course type"
            {...{
              onChange(value) {
                setFilter(data => ({...data, courseType: value}))
              },
            }}
          />
          <FilterDropdown<ICourseStatusOptionID>
            options={courseStatusOptions}
            placeholder="Course status"
            defaultValue="Course status"
            {...{
              onChange(value) {
                setFilter(data => ({...data, courseStatus: value}))
              },
            }}
          />
          {/* <FilterDropdown
            options={lastDateModifiedOptions}
            placeholder="Last date modified"
            defaultValue="Last date modified"
          /> */}
        </SelectContainer>
        <SearchContainer>
          <SearchDropdown
            options={searchOptions}
            placeholder="Search for courses"
            onChange={setSearch}
          />
        </SearchContainer>
      </FiltersContainer>

      {courses.length > 0 ? (
        <CoursesGrid>
          {courses.map((course, index) => (
            <CourseCard
              key={`${course.id}-${index}`}
              {...{
                course, 
                ...courseCardProps,
                onHideCourse(courseId, shouldHide) {
                  const result = courseCardProps.onHideCourse(courseId, shouldHide);
                  setCourses(courses => courses.map(c => c.id === courseId ? {...c, isHidden: shouldHide} : c))
                  return result
                },
                async onDeleteCourse(courseId) {
                  const result =await courseCardProps.onDeleteCourse(courseId);
                  getCourses();
                  return result
                },
              }}
            />
          ))}
        </CoursesGrid>
      ) : (
        <EmptyStateContainer>
          <EmptyStateIcon>
            <span style={{ fontSize: "24px" }}>+</span>
          </EmptyStateIcon>
          <Title style={{ fontSize: "18px", marginBottom: "8px" }}>
            No courses created yet
          </Title>
          <EmptyStateText>
            Your courses will appear here once you've added some. Why not start
            by creating your first course?
          </EmptyStateText>
          <div>
            <AppButton variant="primary" onClick={onMoveToCreateCourse}>
              Create new course
            </AppButton>
          </div>
        </EmptyStateContainer>
      )}
    </Container>
  );
};
